// File: src/components/Admin/InternetServices.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Tab,
  Tabs,
  Tooltip,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Search as SearchIcon,
  Speed as SpeedIcon,
  Router as RouterIcon,
  Cable as CableIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import InternetAvailabilityChecker from './InternetAvailabilityChecker';

interface InternetServiceType {
  id: number;
  service_type: 'VDSL' | 'Fibre' | 'Cable';
  speed_option: string;
  display_name: string;
  download_speed: string;
  upload_speed: string;
  monthly_price: number;
  setup_fee: number;
  is_active: boolean;
  features: string[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`internet-tabpanel-${index}`}
      aria-labelledby={`internet-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const InternetServices: React.FC = () => {
  const [services, setServices] = useState<InternetServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedService, setSelectedService] = useState<InternetServiceType | null>(null);
  const [availabilityDialog, setAvailabilityDialog] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/internetservices');
      setServices(response.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load internet services');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getServiceIcon = (type: string) => {
    switch (type) {
      case 'VDSL':
        return <RouterIcon />;
      case 'Fibre':
        return <SpeedIcon />;
      case 'Cable':
        return <CableIcon />;
      default:
        return <RouterIcon />;
    }
  };

  const getServicesByType = (type: string) => {
    return services.filter(s => s.service_type === type);
  };

  const handleEditService = (service: InternetServiceType) => {
    setSelectedService(service);
    setEditDialog(true);
  };

  const handleUpdateService = async () => {
    if (!selectedService) return;

    try {
      await api.put(`/endpoints/internetservices/${selectedService.id}`, {
        display_name: selectedService.display_name,
        speed_option: selectedService.speed_option,
        download_speed: selectedService.download_speed,
        upload_speed: selectedService.upload_speed,
        monthly_price: selectedService.monthly_price,
        setup_fee: selectedService.setup_fee,
        is_active: selectedService.is_active,
      });

      setSuccess('Service updated successfully');
      setEditDialog(false);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update service');
    }
  };

  const handleDeleteService = async () => {
    if (!selectedService) return;

    try {
      await api.delete(`/endpoints/internetservices/${selectedService.id}`);
      setSuccess('Service deleted successfully');
      setDeleteDialog(false);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete service');
    }
  };

  const handleToggleStatus = async (service: InternetServiceType) => {
    try {
      await api.put(`/endpoints/internetservices/${service.id}`, {
        ...service,
        is_active: !service.is_active,
      });
      setSuccess(`Service ${service.is_active ? 'suspended' : 'activated'} successfully`);
      fetchServices();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update service status');
    }
  };

  const handleOpenDeleteDialog = (service: InternetServiceType) => {
    setSelectedService(service);
    setDeleteDialog(true);
  };

  const ServiceTable = ({ serviceType }: { serviceType: string }) => {
    const typeServices = getServicesByType(serviceType);
    
    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Service</TableCell>
              <TableCell>Download Speed</TableCell>
              <TableCell>Upload Speed</TableCell>
              <TableCell align="right">Monthly Price</TableCell>
              <TableCell align="right">Setup Fee</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {typeServices.map((service) => (
              <TableRow key={service.id}>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getServiceIcon(service.service_type)}
                    <Typography variant="body2">{service.display_name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <DownloadIcon fontSize="small" color="primary" />
                    {service.download_speed}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <UploadIcon fontSize="small" color="secondary" />
                    {service.upload_speed}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">
                    ${service.monthly_price.toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {service.setup_fee > 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      ${service.setup_fee.toFixed(2)}
                    </Typography>
                  ) : (
                    <Chip label="Free" size="small" color="success" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={service.is_active ? 'Active' : 'Inactive'}
                    color={service.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Box display="flex" gap={0.5} justifyContent="center">
                    <Tooltip title="Edit service">
                      <IconButton
                        size="small"
                        onClick={() => handleEditService(service)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={service.is_active ? "Suspend service" : "Activate service"}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(service)}
                        color={service.is_active ? "warning" : "success"}
                      >
                        {service.is_active ? <PauseIcon /> : <PlayIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete service">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(service)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography>Loading internet services...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Internet Services</Typography>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => setAvailabilityDialog(true)}
        >
          Check Availability
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="internet service types">
            <Tab label="VDSL" icon={<RouterIcon />} iconPosition="start" />
            <Tab label="Fibre" icon={<SpeedIcon />} iconPosition="start" />
            <Tab label="Cable Internet" icon={<CableIcon />} iconPosition="start" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <ServiceTable serviceType="VDSL" />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <ServiceTable serviceType="Fibre" />
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <ServiceTable serviceType="Cable" />
          </TabPanel>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Internet Service</DialogTitle>
        <DialogContent>
          {selectedService && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={selectedService.display_name}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    display_name: e.target.value
                  })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Speed Option"
                  value={selectedService.speed_option}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    speed_option: e.target.value
                  })}
                  helperText="e.g., 50M, 100M, 1G"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    value={selectedService.service_type}
                    label="Service Type"
                    onChange={(e) => setSelectedService({
                      ...selectedService,
                      service_type: e.target.value as 'VDSL' | 'Fibre' | 'Cable'
                    })}
                  >
                    <MenuItem value="VDSL">VDSL</MenuItem>
                    <MenuItem value="Fibre">Fibre</MenuItem>
                    <MenuItem value="Cable">Cable</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Download Speed"
                  value={selectedService.download_speed}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    download_speed: e.target.value
                  })}
                  helperText="e.g., 50 Mbps, 1 Gbps"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Upload Speed"
                  value={selectedService.upload_speed}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    upload_speed: e.target.value
                  })}
                  helperText="e.g., 10 Mbps, 1 Gbps"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Monthly Price"
                  type="number"
                  value={selectedService.monthly_price}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    monthly_price: parseFloat(e.target.value) || 0
                  })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Setup Fee"
                  type="number"
                  value={selectedService.setup_fee}
                  onChange={(e) => setSelectedService({
                    ...selectedService,
                    setup_fee: parseFloat(e.target.value) || 0
                  })}
                  InputProps={{
                    startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedService.is_active}
                      onChange={(e) => setSelectedService({
                        ...selectedService,
                        is_active: e.target.checked
                      })}
                    />
                  }
                  label="Service Active"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleUpdateService} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          {selectedService && (
            <Typography>
              Are you sure you want to delete <strong>{selectedService.display_name}</strong>?
              This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteService} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Availability Checker Dialog */}
      <InternetAvailabilityChecker
        open={availabilityDialog}
        onClose={() => setAvailabilityDialog(false)}
      />
    </Box>
  );
};

export default InternetServices;